"use client";

import * as React from "react";
import { usePathname, useSearchParams } from "next/navigation";

/**
 * Top-of-page progress bar.
 *
 * Two triggers:
 * 1. Document-level click listener — if the user clicks an anchor whose href
 *    targets the same origin (i.e. a Next <Link>), the bar starts immediately.
 *    This lets us indicate "loading" *during* the navigation, before the
 *    pathname has changed.
 * 2. Pathname / searchParams change — finalises the bar once the new route
 *    has actually rendered.
 *
 * If a click doesn't result in a navigation (modifier keys, same-href, etc.),
 * a safety timer auto-clears the bar after 8s.
 */
export function NavProgressBar() {
  const pathname = usePathname();
  const search = useSearchParams();
  const [state, setState] = React.useState<"idle" | "loading" | "complete">("idle");
  const lastPath = React.useRef(`${pathname}?${search?.toString() ?? ""}`);
  const safetyTimer = React.useRef<number | null>(null);

  // Start on any link-like click
  React.useEffect(() => {
    function onClick(e: MouseEvent) {
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      // Skip target=_blank, downloads, hash-only, non-same-origin
      if (anchor.target && anchor.target !== "" && anchor.target !== "_self") return;
      if (anchor.hasAttribute("download")) return;
      try {
        const url = new URL(anchor.href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        // Same path + search → no-op nav
        if (url.pathname === window.location.pathname && url.search === window.location.search && !url.hash) return;
      } catch {
        return;
      }
      setState("loading");
      if (safetyTimer.current) window.clearTimeout(safetyTimer.current);
      safetyTimer.current = window.setTimeout(() => setState("idle"), 8000);
    }
    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true } as EventListenerOptions);
  }, []);

  // Finalise on route change
  React.useEffect(() => {
    const key = `${pathname}?${search?.toString() ?? ""}`;
    if (key === lastPath.current) return;
    lastPath.current = key;
    if (safetyTimer.current) window.clearTimeout(safetyTimer.current);
    setState("complete");
    const t = window.setTimeout(() => setState("idle"), 400);
    return () => window.clearTimeout(t);
  }, [pathname, search]);

  if (state === "idle") return null;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed left-0 right-0 top-0 z-[60] h-0.5"
    >
      <div
        className={
          state === "loading"
            ? "h-full bg-primary animate-[nav-progress_8s_ease-out_forwards]"
            : "h-full w-full bg-primary opacity-0 transition-opacity duration-300"
        }
      />
      <style jsx>{`
        @keyframes nav-progress {
          0%   { width: 0%; }
          10%  { width: 30%; }
          40%  { width: 65%; }
          70%  { width: 85%; }
          100% { width: 95%; }
        }
      `}</style>
    </div>
  );
}
